// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the presentational rail bar (rail B: a translucent pill that hugs its
// icon tabs, with the ink Ask circle at the end).
// Settings > Mobile rail renders it with `preview` for the live preview, so
// it must stay free of routing and store reads: everything comes in through
// props. The bar never positions itself; MobileRail owns the fixed host and
// `embedded` draws it as the bar row at the foot of the grown panel.

import styles from '@app/features/app/components/layout/mobile_rail/MobileRailBar.module.css';
import {
	MOBILE_RAIL_TAB_LABELS,
	MobileRailTabIcon,
} from '@app/features/app/components/layout/mobile_rail/MobileRailTabLabels';
import type {MobileRailTabId} from '@app/features/app/components/layout/mobile_rail/MobileRailTypes';
import {SparkleIcon} from '@app/features/ui/icons/Icons';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {type ReactNode, useCallback, useEffect, useRef} from 'react';

export interface MobileRailBarProps {
	tabs: ReadonlyArray<MobileRailTabId>;
	activeTab: MobileRailTabId | null;
	/** Unread counts per tab; missing or 0 draws no badge. */
	counts?: Partial<Record<MobileRailTabId, number>>;
	/** Dots for tabs that have "something new" without a count. */
	dots?: Partial<Record<MobileRailTabId, boolean>>;
	/** Ask circle drawn pressed/open. */
	askActive?: boolean;
	/** A corner suggestion is waiting (konvo_corner): a dot on the Ask circle. */
	askDot?: boolean;
	/** Unread messages from Konvo: a number on the Ask circle, drawn instead of the dot. */
	askCount?: number;
	/** Voice mode is running: Ask widens and shows this (its level and time) instead of the sparkle. */
	askLive?: ReactNode;
	/** Static rendering for the settings preview: no interaction, not fixed to the viewport. */
	preview?: boolean;
	/** Bar row inside the grown panel: no pill chrome, a divider on top. */
	embedded?: boolean;
	onTab?: (tab: MobileRailTabId) => void;
	onAsk?: () => void;
	/** Ask held for 500ms: the way into the More panel when More is not on the rail. */
	onAskLongPress?: () => void;
}

const ASK_ARIA_DESCRIPTOR = msg({message: 'Ask Konvo', comment: 'Aria label for the phone rail Ask pill.'});
const ASK_UNREAD_DESCRIPTOR = msg({
	message: 'Ask Konvo, {count} new from Konvo',
	comment: 'Aria label of the phone rail Ask pill while Konvo messages are unread. count is a number or "99+".',
});
const ASK_LIVE_ARIA_DESCRIPTOR = msg({
	message: 'Voice controls',
	comment: 'Aria label for the phone rail Ask pill while voice mode runs; a tap shows mute, type instead and end.',
});
const TAB_UNREAD_DESCRIPTOR = msg({
	message: '{label}, {count} unread',
	comment: 'Aria label of a phone rail tab with a badge. label is the tab name, count is a number or "99+".',
});

const MAX_BADGE = 99;
const ICON_SIZE = 22;
const BRAND_ICON_SIZE = 20;
const ASK_HOLD_MS = 500;

function formatBadge(count: number): string {
	return count > MAX_BADGE ? `${MAX_BADGE}+` : String(count);
}

export function MobileRailBar({
	tabs,
	activeTab,
	counts,
	dots,
	askActive = false,
	askDot = false,
	askCount = 0,
	askLive,
	preview = false,
	embedded = false,
	onTab,
	onAsk,
	onAskLongPress,
}: MobileRailBarProps) {
	const {i18n} = useLingui();
	const inert = preview;
	const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const holdFired = useRef(false);
	const cancelAskHold = useCallback(() => {
		if (holdTimer.current) clearTimeout(holdTimer.current);
		holdTimer.current = null;
	}, []);
	const startAskHold = useCallback(() => {
		holdFired.current = false;
		cancelAskHold();
		holdTimer.current = setTimeout(() => {
			holdTimer.current = null;
			holdFired.current = true;
			onAskLongPress?.();
		}, ASK_HOLD_MS);
	}, [cancelAskHold, onAskLongPress]);
	const handleAskClick = useCallback(() => {
		if (holdFired.current) {
			holdFired.current = false;
			return;
		}
		onAsk?.();
	}, [onAsk]);
	useEffect(() => cancelAskHold, [cancelAskHold]);
	return (
		<div
			className={clsx(
				styles.bar,
				!embedded && styles.compact,
				embedded && styles.embedded,
				preview && styles.preview,
				tabs.length >= 5 && styles.five,
			)}
			aria-hidden={inert || undefined}
			data-flx="app.mobile-rail.bar"
		>
			{tabs.map((tab) => {
				const count = counts?.[tab] ?? 0;
				const dot = !count && Boolean(dots?.[tab]);
				const active = activeTab === tab;
				const label = i18n._(MOBILE_RAIL_TAB_LABELS[tab]);
				const brand = tab === 'whatsapp' || tab === 'clickup';
				return (
					<button
						key={tab}
						type="button"
						className={clsx(styles.tab, active && styles.on)}
						aria-label={count > 0 ? i18n._(TAB_UNREAD_DESCRIPTOR, {label, count: formatBadge(count)}) : label}
						aria-current={active && tab !== 'more' ? 'page' : undefined}
						aria-pressed={tab === 'more' || tab === 'scratchpad' ? active : undefined}
						tabIndex={inert ? -1 : undefined}
						onClick={inert ? undefined : () => onTab?.(tab)}
						data-tab={tab}
						data-flx={`app.mobile-rail.bar.tab.${tab}`}
					>
						<MobileRailTabIcon tab={tab} size={brand ? BRAND_ICON_SIZE : ICON_SIZE} mono className={styles.icon} />
						{count > 0 && (
							<span className={styles.badge} aria-hidden data-flx="app.mobile-rail.bar.badge">
								{formatBadge(count)}
							</span>
						)}
						{dot && <span className={styles.dot} aria-hidden data-flx="app.mobile-rail.bar.dot" />}
					</button>
				);
			})}
			<button
				type="button"
				className={clsx(styles.ask, askActive && styles.askOn, askLive != null && styles.askLive)}
				aria-label={
					askLive != null
						? i18n._(ASK_LIVE_ARIA_DESCRIPTOR)
						: askCount > 0
							? i18n._(ASK_UNREAD_DESCRIPTOR, {count: formatBadge(askCount)})
							: i18n._(ASK_ARIA_DESCRIPTOR)
				}
				aria-pressed={askActive}
				tabIndex={inert ? -1 : undefined}
				onClick={inert ? undefined : handleAskClick}
				onPointerDown={inert || !onAskLongPress ? undefined : startAskHold}
				onPointerUp={cancelAskHold}
				onPointerCancel={cancelAskHold}
				onPointerLeave={cancelAskHold}
				onContextMenu={onAskLongPress ? (event) => event.preventDefault() : undefined}
				data-flx="app.mobile-rail.bar.ask"
			>
				{askLive ?? <SparkleIcon className={styles.askIcon} data-flx="app.mobile-rail.bar.ask-icon" />}
				{askLive != null ? null : askCount > 0 ? (
					<span className={styles.askCount} aria-hidden data-flx="app.mobile-rail.bar.ask-count">
						{formatBadge(askCount)}
					</span>
				) : (
					askDot && <span className={styles.askDot} aria-hidden data-flx="app.mobile-rail.bar.ask-dot" />
				)}
			</button>
		</div>
	);
}
